(() => {
  if (customElements.get('pdp-shop-video')) return;

  class ProductShopVideo extends HTMLElement {
    connectedCallback() {
      this.initFrame = requestAnimationFrame(() => this.initialize());
    }

    initialize() {
      if (!this.isConnected || this.events) return;
      this.list = this.querySelector('.psv-list-wrapper');
      this.dialog = this.querySelector('dialog');
      this.thumbs = [...this.querySelectorAll('.js-psv-thumb')];
      this.slides = [...this.querySelectorAll('.js-psv-modal-slide')];
      if (!this.list || !this.dialog || !this.thumbs.length) return;

      this.events = new AbortController();
      this.videos ||= new Map();
      const options = { signal: this.events.signal };
      this.form = this.dialog.querySelector('form');
      this.variantSelect = this.form.querySelector('.js-psv-variant-select');
      this.submitButton = this.form.querySelector('[type="submit"]');
      this.previewIndex = 0;
      this.modalIndex = 0;
      this.visible = false;
      this.modalOpen = false;
      this.muted = false;
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

      this.addEventListener('click', (event) => this.handleClick(event), options);
      this.addEventListener('timeupdate', (event) => this.updateProgress(event.target), { ...options, capture: true });
      this.addEventListener('ended', (event) => this.advancePreview(event.target), { ...options, capture: true });
      for (const type of ['loadstart', 'waiting', 'seeking', 'playing', 'canplay', 'seeked', 'pause', 'ended', 'error']) {
        this.addEventListener(type, (event) => this.updateMediaStatus(event), { ...options, capture: true });
      }
      this.list.addEventListener('scroll', () => this.updateListNavigation(), { ...options, passive: true });
      this.form.addEventListener('submit', (event) => this.addToCart(event), options);
      this.variantSelect?.addEventListener('change', () => this.updateVariant(), options);
      this.dialog.addEventListener('close', () => {
        if (!this.dialog.open) this.finishModal();
      }, options);
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.closeModal();
      }, options);
      document.addEventListener('visibilitychange', () => this.syncPlayback(), options);
      this.reducedMotion.addEventListener('change', () => this.syncPlayback(), options);

      if ('IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver((entries) => {
          this.visible = entries.some((entry) => entry.isIntersecting);
          this.syncPlayback();
        });
        this.visibilityObserver.observe(this.list);
      } else {
        this.visible = true;
      }
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.updateListNavigation());
        this.resizeObserver.observe(this.list);
      } else {
        window.addEventListener('resize', () => this.updateListNavigation(), options);
      }
      this.updateListNavigation();
      this.updateVariant();
      this.syncPlayback();
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.initFrame);
      this.events?.abort();
      this.events = null;
      this.visibilityObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.stopPlayback();
      if (this.dialog?.open) this.dialog.close();
      this.finishModal(false);
      queueMicrotask(() => {
        if (this.isConnected) return;
        this.videos?.forEach((video) => {
          video.pause();
          video.removeAttribute('src');
          video.load();
          video.remove();
        });
        this.videos?.clear();
      });
    }

    getVideo(container) {
      const template = container.querySelector('template[data-video]').content.querySelector('video');
      const source = template.dataset.videoUrl;
      if (!source) return null;
      const url = new URL(source, document.baseURI).href;
      let video = this.videos.get(url);
      if (!video) {
        video = template.cloneNode(true);
        video.preload = 'auto';
        this.videos.set(url, video);
      }
      video.muted = !this.modalOpen || this.muted;
      video.loop = this.modalOpen || this.thumbs.length === 1;
      video.controls = this.modalOpen;
      video.className = this.modalOpen ? 'psv-full-video' : 'psv-preview-video';
      if (video.parentElement !== container) container.append(video);
      return video;
    }

    setVideoStatus(container, state) {
      let status = container.querySelector('.psv-video-status');
      if (!status && state) {
        status = this.querySelector('template[data-video-status]').content.firstElementChild.cloneNode(true);
        if (!container.matches('.js-psv-modal-slide')) status.querySelector('button').remove();
        container.append(status);
      }
      container.setAttribute('aria-busy', String(state === 'loading'));
      if (!status) return;
      status.hidden = !state;
      const label = status.querySelector('.psv-video-status__label');
      label.textContent = state === 'error' ? this.dataset.videoErrorLabel : this.dataset.videoLoadingLabel;
      label.classList.toggle('visually-hidden', state !== 'error');
      status.querySelector('.spinner').hidden = state !== 'loading';
      const retry = status.querySelector('button');
      if (retry) retry.hidden = state !== 'error';
    }

    updateMediaStatus(event) {
      const video = event.target;
      if (video !== this.activeVideo || !this.activeContainer) return;
      if (video.error || event.type === 'error') {
        this.setVideoStatus(this.activeContainer, 'error');
      } else if (event.type === 'loadstart' || event.type === 'seeking' || event.type === 'waiting') {
        if (!video.paused) this.setVideoStatus(this.activeContainer, 'loading');
      } else {
        this.setVideoStatus(this.activeContainer, '');
      }
    }

    stopPlayback() {
      this.playbackVersion = (this.playbackVersion || 0) + 1;
      this.activeVideo?.pause();
      if (this.activeContainer) this.setVideoStatus(this.activeContainer, '');
      this.activeVideo = null;
      this.activeContainer = null;
    }

    syncPlayback({ retry = false } = {}) {
      if (!this.isConnected || document.hidden || (!this.modalOpen && (!this.visible || this.reducedMotion.matches))) {
        this.stopPlayback();
        return;
      }
      const container = this.modalOpen ? this.slides[this.modalIndex] : this.thumbs[this.previewIndex];
      if (this.activeContainer === container && !retry) return;
      this.stopPlayback();
      this.activeContainer = container;
      const video = this.getVideo(container);
      if (!video) {
        this.setVideoStatus(container, 'error');
        return;
      }
      this.activeVideo = video;
      if (!video.hasAttribute('src')) {
        video.src = video.dataset.videoUrl;
      } else if (retry) {
        video.load();
      }
      if (video.error && !retry) {
        this.setVideoStatus(container, 'error');
        return;
      }
      if (video.ended) video.currentTime = 0;
      this.setVideoStatus(container, video.readyState < 3 ? 'loading' : '');
      const version = this.playbackVersion;
      Promise.resolve(video.play()).then(() => {
        if (this.activeVideo !== video) {
          video.pause();
        } else if (this.playbackVersion === version) {
          this.setVideoStatus(container, '');
        }
      }).catch((error) => {
        if (this.playbackVersion !== version || this.activeVideo !== video) return;
        if (error.name === 'NotAllowedError' || error.name === 'AbortError') {
          this.setVideoStatus(container, '');
          if (this.modalOpen) video.controls = true;
        } else {
          this.setVideoStatus(container, 'error');
        }
      });
    }

    updateProgress(video) {
      if (this.modalOpen || this.activeVideo !== video || video.paused) return;
      const bar = this.thumbs[this.previewIndex].querySelector('.js-psv-progress');
      const progress = video.currentTime / video.duration;
      bar.style.width = `${Number.isFinite(progress) ? Math.min(100, progress * 100) : 0}%`;
    }

    advancePreview(video) {
      if (this.modalOpen || this.activeVideo !== video || this.thumbs.length < 2) return;
      this.thumbs[this.previewIndex].querySelector('.js-psv-progress').style.width = '0%';
      this.stopPlayback();
      this.previewIndex = (this.previewIndex + 1) % this.thumbs.length;
      const thumb = this.thumbs[this.previewIndex];
      const left = this.list.scrollLeft + thumb.getBoundingClientRect().left - this.list.getBoundingClientRect().left;
      this.list.scrollTo({ left, behavior: this.reducedMotion.matches ? 'auto' : 'smooth' });
      this.syncPlayback();
    }

    updateListNavigation() {
      this.querySelector('.js-psv-list-prev').disabled = this.list.scrollLeft <= 10;
      this.querySelector('.js-psv-list-next').disabled = this.list.scrollLeft >= this.list.scrollWidth - this.list.clientWidth - 10;
    }

    handleClick(event) {
      const thumb = event.target.closest('.js-psv-thumb');
      if (thumb && this.contains(thumb)) {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || typeof this.dialog.showModal !== 'function') return;
        event.preventDefault();
        this.openModal(Number(thumb.dataset.index), thumb);
        return;
      }
      const button = event.target.closest('button');
      if (event.target === this.dialog || button?.matches('.js-psv-close')) this.closeModal();
      if (!button || button.disabled) return;
      if (button.matches('.js-psv-retry')) {
        this.syncPlayback({ retry: true });
      } else if (button.matches('.js-psv-list-prev, .js-psv-list-next')) {
        const direction = button.matches('.js-psv-list-prev') ? -1 : 1;
        this.list.scrollBy({ left: direction * this.list.clientWidth * 0.8, behavior: this.reducedMotion.matches ? 'auto' : 'smooth' });
      } else if (button.matches('.js-psv-prev, .js-psv-next')) {
        this.selectSlide(this.modalIndex + (button.matches('.js-psv-prev') ? -1 : 1));
      } else if (button.matches('.js-psv-mute-toggle')) {
        this.muted = !this.muted;
        button.classList.toggle('is-muted', this.muted);
        button.setAttribute('aria-pressed', String(this.muted));
        if (this.activeVideo) this.activeVideo.muted = this.muted;
      } else if (button.matches('.js-psv-qty-minus, .js-psv-qty-plus')) {
        const input = this.form.querySelector('[name="quantity"]');
        const quantity = Number.parseInt(input.value, 10) || 1;
        input.value = Math.max(1, quantity + (button.matches('.js-psv-qty-minus') ? -1 : 1));
      }
    }

    openModal(index, trigger) {
      this.returnFocus = trigger;
      this.modalOpen = true;
      this.previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this.dialog.showModal();
      this.dialog.classList.add('is-active');
      this.selectSlide(index);
      this.dialog.querySelector('.js-psv-close').focus({ preventScroll: true });
    }

    selectSlide(index) {
      if (index < 0 || index >= this.slides.length) return;
      this.modalIndex = index;
      this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
      this.dialog.querySelector('.js-psv-prev').disabled = index === 0;
      this.dialog.querySelector('.js-psv-next').disabled = index === this.slides.length - 1;
      if (this.variantSelect) this.variantSelect.value = this.slides[index].dataset.variantId;
      this.updateVariant();
      this.syncPlayback();
    }

    closeModal() {
      if (this.dialog.open) this.dialog.close();
      this.finishModal();
    }

    finishModal(resume = true) {
      if (!this.modalOpen) return;
      this.stopPlayback();
      this.modalOpen = false;
      this.dialog.classList.remove('is-active');
      document.body.style.overflow = this.previousOverflow;
      this.previewIndex = this.modalIndex;
      if (resume && this.isConnected) {
        this.returnFocus?.focus({ preventScroll: true });
        this.syncPlayback();
      }
    }

    updateVariant() {
      const option = this.variantSelect?.selectedOptions[0];
      if (option) {
        this.form.querySelector('[name="id"]').value = option.value;
        this.form.querySelector('.js-psv-price').textContent = option.dataset.price;
        const image = this.form.querySelector('.js-psv-prod-img');
        if (option.dataset.img && image.getAttribute('src') !== option.dataset.img) image.src = option.dataset.img;
        this.soldOut = option.dataset.available === 'false';
      } else if (this.soldOut === undefined) {
        this.soldOut = this.submitButton.disabled;
      }
      this.submitButton.disabled = this.pending || this.soldOut;
      if (!this.pending) this.submitButton.textContent = this.soldOut ? this.submitButton.dataset.soldOutLabel : this.submitButton.dataset.addLabel;
    }

    async addToCart(event) {
      event.preventDefault();
      if (this.pending || this.soldOut || !this.form.reportValidity()) return;
      this.pending = true;
      this.submitButton.disabled = true;
      this.submitButton.textContent = this.dataset.addingLabel;
      const message = this.form.querySelector('.psv-error');
      message.hidden = true;
      const root = window.Shopify?.routes?.root || '/';
      const drawer = document.querySelector('cart-drawer');
      const section = drawer?.getAttribute('section') || 'mini-cart';
      const formData = new FormData(this.form);
      formData.set('sections', section);
      formData.set('sections_url', window.location.pathname);
      try {
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST', body: formData, headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });
        const added = await response.json();
        if (!response.ok) throw new Error(added.description || added.message || this.dataset.errorLabel);
        if (window.themeVariables?.settings?.cartType === 'page') {
          window.location.assign(`${root}cart`);
          return;
        }
        this.closeModal();
        let cart;
        try {
          const cartResponse = await fetch(`${root}cart.js`);
          if (cartResponse.ok) cart = await cartResponse.json();
        } catch (_) {}
        if (cart) {
          cart.sections = added.sections;
          document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart } }));
        }
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true, detail: { cart, openMiniCart: true, replacementDelay: 0 }
        }));
      } catch (error) {
        message.textContent = error.message;
        message.hidden = false;
      } finally {
        this.pending = false;
        this.updateVariant();
      }
    }
  }

  customElements.define('pdp-shop-video', ProductShopVideo);
})();
